import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Archive, Mail, Phone, X, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import type { Contact } from '../lib/types';
import { formatDate } from '../lib/format';

export default function Contacts() {
  const { currentOrg } = useOrg();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filter, setFilter] = useState<'all' | 'customer' | 'supplier'>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);

  async function refresh() {
    if (!currentOrg) return;
    const { data, error } = await supabase
      .from('dodobooks_contacts')
      .select('*')
      .eq('org_id', currentOrg.id)
      .eq('is_archived', false)
      .order('name');
    if (!error) setContacts((data ?? []) as Contact[]);
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentOrg]);

  const filtered = contacts
    .filter((c) => filter === 'all' || (filter === 'customer' ? c.is_customer : c.is_supplier))
    .filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.email ?? '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="h-page">Contacts</h1>
          <p className="text-text-dim text-sm mt-1">Customers, suppliers, everyone you do business with.</p>
        </div>
        <button onClick={() => setEditing('new')} className="btn btn-primary"><Plus size={14} /> Add contact</button>
      </header>

      <div className="flex gap-3 items-center">
        <div className="flex bg-surface border border-line rounded-md overflow-hidden">
          {(['all', 'customer', 'supplier'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={
                'px-4 py-1.5 text-xs font-mono uppercase tracking-wider ' +
                (filter === f ? 'bg-emerald/10 text-emerald' : 'text-text-mute hover:text-text')
              }
            >{f}</button>
          ))}
        </div>
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-mute" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts…"
            className="pl-9 w-full"
          />
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-text-mute text-sm py-12 text-center">
            No contacts yet. <button onClick={() => setEditing('new')} className="text-emerald hover:underline">Add your first one.</button>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="px-5">Name</th>
                <th>VAT no.</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Type</th>
                <th>Terms</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="group">
                  <td className="px-5 font-medium cursor-pointer" onClick={() => setEditing(c)}>{c.name}</td>
                  <td className="num text-text-dim cursor-pointer" onClick={() => setEditing(c)}>{c.vat_number || '—'}</td>
                  <td className="text-text-dim cursor-pointer" onClick={() => setEditing(c)}>{c.email || '—'}</td>
                  <td className="text-text-dim cursor-pointer" onClick={() => setEditing(c)}>{c.phone || '—'}</td>
                  <td className="cursor-pointer" onClick={() => setEditing(c)}>
                    {c.is_customer && <span className="pill pill-paid">Customer</span>}
                    {c.is_supplier && <span className="pill pill-sent ml-1">Supplier</span>}
                  </td>
                  <td className="text-text-dim cursor-pointer" onClick={() => setEditing(c)}>{c.default_terms_days}d</td>
                  <td className="text-text-mute cursor-pointer" onClick={() => setEditing(c)}>{formatDate(c.created_at)}</td>
                  <td className="pr-5">
                    {c.is_customer && (
                      <Link to={`/contacts/${c.id}/statement`} className="text-text-mute hover:text-emerald inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity text-xs" title="View statement">
                        <FileText size={12} /> Statement
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <ContactDrawer
          contact={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { refresh(); setEditing(null); }}
        />
      )}
    </div>
  );
}

function ContactDrawer({ contact, onClose, onSaved }: { contact: Contact | null; onClose: () => void; onSaved: () => void }) {
  const { currentOrg } = useOrg();
  const [form, setForm] = useState<Partial<Contact>>({
    name: contact?.name ?? '',
    is_customer: contact?.is_customer ?? true,
    is_supplier: contact?.is_supplier ?? false,
    vat_number: contact?.vat_number ?? '',
    reg_number: contact?.reg_number ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    address: contact?.address ?? '',
    city: contact?.city ?? '',
    province: contact?.province ?? '',
    postal_code: contact?.postal_code ?? '',
    country: contact?.country ?? 'ZA',
    currency: contact?.currency ?? 'ZAR',
    default_terms_days: contact?.default_terms_days ?? 30,
    notes: contact?.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!currentOrg) return;
    setBusy(true); setErr(null);

    const payload = {
      org_id: currentOrg.id,
      name: form.name?.trim(),
      is_customer: !!form.is_customer,
      is_supplier: !!form.is_supplier,
      vat_number: form.vat_number?.trim() || null,
      reg_number: form.reg_number?.trim() || null,
      email: form.email?.trim() || null,
      phone: form.phone?.trim() || null,
      address: form.address?.trim() || null,
      city: form.city?.trim() || null,
      province: form.province?.trim() || null,
      postal_code: form.postal_code?.trim() || null,
      country: form.country || 'ZA',
      currency: form.currency || 'ZAR',
      default_terms_days: Number(form.default_terms_days ?? 30),
      notes: form.notes?.trim() || null,
    };

    const op = contact
      ? supabase.from('dodobooks_contacts').update(payload).eq('id', contact.id)
      : supabase.from('dodobooks_contacts').insert(payload);
    const { error } = await op;
    setBusy(false);
    if (error) setErr(error.message);
    else onSaved();
  }

  async function archive() {
    if (!contact) return;
    await supabase.from('dodobooks_contacts').update({ is_archived: true }).eq('id', contact.id);
    onSaved();
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-stretch justify-end">
      <div className="bg-surface w-full max-w-xl border-l border-line-strong overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-line sticky top-0 bg-surface z-10">
          <h2 className="h-section">{contact ? 'Edit contact' : 'Add contact'}</h2>
          <button onClick={onClose} className="text-text-mute hover:text-text"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 grid grid-cols-2 gap-4">
          <div className="field col-span-2">
            <label>Name *</label>
            <input required value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="col-span-2 flex gap-4">
            <label className="flex items-center gap-2 text-sm font-sans normal-case tracking-normal text-text">
              <input type="checkbox" className="accent-emerald" checked={!!form.is_customer} onChange={(e) => setForm({ ...form, is_customer: e.target.checked })} />
              Customer
            </label>
            <label className="flex items-center gap-2 text-sm font-sans normal-case tracking-normal text-text">
              <input type="checkbox" className="accent-emerald" checked={!!form.is_supplier} onChange={(e) => setForm({ ...form, is_supplier: e.target.checked })} />
              Supplier
            </label>
          </div>
          <div className="field"><label>Email</label><input type="email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="field"><label>Phone</label><input type="tel" value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="field"><label>VAT number</label><input value={form.vat_number ?? ''} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
          <div className="field"><label>Reg. number</label><input value={form.reg_number ?? ''} onChange={(e) => setForm({ ...form, reg_number: e.target.value })} /></div>
          <div className="field col-span-2"><label>Street address</label><input value={form.address ?? ''} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="field"><label>City</label><input value={form.city ?? ''} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          <div className="field"><label>Province</label><input value={form.province ?? ''} onChange={(e) => setForm({ ...form, province: e.target.value })} /></div>
          <div className="field"><label>Postal code</label><input value={form.postal_code ?? ''} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
          <div className="field"><label>Terms (days)</label><input type="number" value={form.default_terms_days ?? 30} onChange={(e) => setForm({ ...form, default_terms_days: Number(e.target.value) })} /></div>
          <div className="field col-span-2"><label>Notes</label><textarea rows={3} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}

          <div className="col-span-2 flex items-center justify-between pt-2">
            <div>
              {contact && (
                <button type="button" onClick={archive} className="btn btn-ghost text-rust"><Archive size={14} /> Archive</button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
              <button type="submit" disabled={busy || !form.name?.trim()} className="btn btn-primary disabled:opacity-50">
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>

        {contact && (
          <div className="px-5 pb-5 text-xs text-text-mute font-mono space-y-1">
            {contact.email && <div className="flex items-center gap-1.5"><Mail size={11} /> {contact.email}</div>}
            {contact.phone && <div className="flex items-center gap-1.5"><Phone size={11} /> {contact.phone}</div>}
            <div>Created {formatDate(contact.created_at)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
